import ExcelJS from "exceljs";
import {
  buildBankSheet,
  buildJvSheet,
  buildRegisterSheet,
  buildSalarySummarySheet,
  downloadWorkbook,
  fileMonthLabel,
  formatPayMonthLabel,
  getCompanyName,
  mergeComponentOrder,
  styleLightHeader,
  applyDataBorder,
  moneyFmt,
  addTitleBlock,
  REPORT_COLORS,
  roundMoney,
  applyHeaderBasedColumnWidths,
} from "./payrollReportExcelShared";

function resolveOrders(data, options = {}) {
  const fixedEarnings = mergeComponentOrder(
    options.columnOrder?.fixedEarningOrder || options.columnOrder?.fixedOrder,
    data.fixedEarningComponentNames?.length
      ? data.fixedEarningComponentNames
      : (data.fixedComponentNames || []).filter(
          (n) => !/\(employer\)/i.test(n) && !/gratuity/i.test(n)
        )
  );
  const benefits = mergeComponentOrder(
    options.columnOrder?.benefitOrder,
    data.benefitComponentNames?.length
      ? data.benefitComponentNames
      : (data.fixedComponentNames || []).filter(
          (n) => /\(employer\)/i.test(n) || /gratuity/i.test(n)
        )
  );
  const paidFixed = mergeComponentOrder(
    options.columnOrder?.paidFixedOrder || options.columnOrder?.earningOrder,
    data.paidFixedComponentNames?.length
      ? data.paidFixedComponentNames
      : data.earningComponentNames || []
  );
  const rawVariables = mergeComponentOrder(
    options.columnOrder?.variableOrder,
    data.variableComponentNames || []
  ).filter((n) => !/lop\s*reversal\s*\(/i.test(String(n)));
  const variables = rawVariables.some((n) => /^arrears$/i.test(String(n)))
    ? rawVariables
    : [...rawVariables, "Arrears"];
  // Avoid duplicating variable names inside paid-fixed when falling back to old earning list
  const paidFixedDeduped = paidFixed.filter(
    (n) =>
      !variables.some((v) => String(v).toLowerCase() === String(n).toLowerCase()) &&
      !/lop\s*reversal\s*\(/i.test(String(n))
  );
  const deductions = mergeComponentOrder(
    options.columnOrder?.deductionOrder,
    data.deductionComponentNames
  ).filter((n) => !/lop\s*reversal\s*\(/i.test(String(n)));
  if (!deductions.some((n) => /^arr\.?\s*pf\b/i.test(String(n)))) deductions.push("Arr.PF");
  if (!deductions.some((n) => /^arr\.?\s*esi\b/i.test(String(n)))) deductions.push("Arr.ESI");
  return {
    orderedFixedEarnings: fixedEarnings,
    orderedBenefits: benefits,
    orderedPaidFixed: paidFixedDeduped,
    orderedVariables: variables,
    orderedDeductions: deductions,
  };
}

function metaFromGate(data) {
  const generatedAt = new Date().toLocaleString("en-IN");
  return `Processed: ${data.processedCount || data.rows?.length || 0} of ${
    data.scopeCount || data.totalInScope || data.rows?.length || 0
  }  |  Generated: ${generatedAt}`;
}

/** Payroll Projection — Sheet 1 Register (projection payable days) + Sheet 2 Salary Summary */
export async function exportPayrollProjectionExcel(data, options = {}) {
  const rows = data?.rows || [];
  if (!rows.length) throw new Error("No employees to export in Payroll Projection");

  const companyName = getCompanyName();
  const monthLabel = formatPayMonthLabel(data.payMonth);
  const fileLabel = fileMonthLabel(data.payMonth);
  const metaLine = `${metaFromGate(data)}  |  Payable days: DOJ/DOE projection`;
  const { orderedFixedEarnings, orderedBenefits, orderedPaidFixed, orderedVariables, orderedDeductions } =
    resolveOrders(data, options);

  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  buildRegisterSheet(wb, {
    sheetName: "Payroll Projection",
    companyName,
    title: `Payroll Projection — ${monthLabel}`,
    metaLine,
    rows,
    orderedFixedEarnings,
    orderedBenefits,
    orderedPaidFixed,
    orderedVariables,
    orderedDeductions,
    includeTotalCost: true,
    includeTotalCashFlow: true,
  });

  buildSalarySummarySheet(wb, {
    companyName,
    title: `Salary Summary — ${monthLabel}`,
    metaLine,
    summary: data.summary,
  });

  return downloadWorkbook(wb, `Payroll Projection ${fileLabel}.xlsx`);
}

/** Payroll Register — Sheet 1 Register + Sheet 2 Summary + Sheet 3 Bank */
export async function exportPayrollRegisterExcel(data, options = {}) {
  const rows = data?.rows || [];
  if (!rows.length) throw new Error("No processed employees to export");

  const companyName = getCompanyName();
  const monthLabel = formatPayMonthLabel(data.payMonth);
  const fileLabel = fileMonthLabel(data.payMonth);
  const metaLine = metaFromGate(data);
  const { orderedFixedEarnings, orderedBenefits, orderedPaidFixed, orderedVariables, orderedDeductions } =
    resolveOrders(data, options);

  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  buildRegisterSheet(wb, {
    sheetName: "Salary Register",
    companyName,
    title: `Payroll Register — ${monthLabel}`,
    metaLine,
    rows,
    orderedFixedEarnings,
    orderedBenefits,
    orderedPaidFixed,
    orderedVariables,
    orderedDeductions,
    includeTotalCost: true,
    includeTotalCashFlow: true,
  });

  buildSalarySummarySheet(wb, {
    companyName,
    title: `Salary Summary — ${monthLabel}`,
    metaLine,
    summary: data.summary,
  });

  buildBankSheet(wb, {
    companyName,
    title: `Bank Disbursement Statement — ${monthLabel}`,
    metaLine,
    bankStatement: data.bankStatement,
    rows,
  });

  return downloadWorkbook(wb, `Payroll Register ${fileLabel}.xlsx`);
}

/** Classic Reco Report (Opening A / Joiners B / Left C / Adjustments D / Closing) + reasons */
export async function exportPayrollReconciliationExcel(data) {
  const recoReports = data?.recoReports || [];
  if (!recoReports.length) {
    throw new Error("No reconciliation data to export");
  }

  const companyName = getCompanyName();
  const fileLabel = fileMonthLabel(data.payMonth);
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  const styleSection = (row) => {
    row.font = { bold: true, size: 11, color: { argb: "FF1E3A5F" } };
    row.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0F2FE" } };
      applyDataBorder(cell);
    });
  };

  const pushEmpRow = (ws, emp, earningCols, deductionCols, moneyEndCol) => {
    const values = [
      emp.empCode || "",
      emp.name || "",
      ...(emp.earnings || earningCols.map(() => 0)).map((v) => roundMoney(v)),
      roundMoney(emp.earningTotal),
      ...(emp.deductions || deductionCols.map(() => 0)).map((v) => roundMoney(v)),
      roundMoney(emp.deductionTotal),
      roundMoney(emp.net),
      emp.headCount ?? 1,
      emp.category || "",
      emp.reason || "",
    ];
    const row = ws.addRow(values);
    row.eachCell((cell, c) => {
      applyDataBorder(cell);
      if (c >= 3 && c <= moneyEndCol) moneyFmt(cell);
    });
    // Reason: single line (width fitted later from longest reason)
    const reasonCell = row.getCell(values.length);
    reasonCell.alignment = { wrapText: false, vertical: "middle", horizontal: "left" };
    return row;
  };

  const pushTotalsRow = (ws, label, totals, earningCols, deductionCols, moneyEndCol) => {
    const values = [
      "",
      label,
      ...(totals.earnings || earningCols.map(() => 0)).map((v) => roundMoney(v)),
      roundMoney(totals.earningTotal),
      ...(totals.deductions || deductionCols.map(() => 0)).map((v) => roundMoney(v)),
      roundMoney(totals.deductionTotal),
      roundMoney(totals.net),
      totals.headCount ?? "",
      "",
      "",
    ];
    const row = ws.addRow(values);
    row.font = { bold: true };
    row.eachCell((cell, c) => {
      styleLightHeader(cell, "FFFEF3C7");
      if (c >= 3 && c <= moneyEndCol) moneyFmt(cell);
    });
    return row;
  };

  recoReports.forEach((reco, idx) => {
    const sheetName =
      idx === 0
        ? "Reco Report"
        : `Reco vs ${String(reco.comparePayMonth || "").slice(5)}`.slice(0, 31);
    const earn = reco.earningColumns || [];
    const ded = reco.deductionColumns || [];
    const headers = [
      "Emp ID",
      "Employee Name",
      ...earn,
      "Earning Total",
      ...ded,
      "Deduction Total",
      "Net Amount",
      "Head Count",
      "Reason Category",
      "Reason (why the difference)",
    ];
    const moneyEndCol = 2 + earn.length + 1 + ded.length + 1 + 1; // through Net Amount
    const reasonCol = headers.length;

    const ws = wb.addWorksheet(sheetName, {
      views: [{ state: "frozen", ySplit: 4, xSplit: 2, showGridLines: false }],
    });

    const titleRow = ws.addRow([companyName]);
    titleRow.font = { bold: true, size: 15, color: { argb: "FF1E3A5F" } };
    ws.mergeCells(1, 1, 1, Math.max(headers.length, 4));

    const sub = ws.addRow([
      `Salary Reconciliation For Month ${reco.currentLabel}  (vs ${reco.compareLabel})`,
    ]);
    sub.font = { bold: true, size: 12, color: { argb: "FF374151" } };
    ws.mergeCells(2, 1, 2, Math.max(headers.length, 4));

    const tie = reco.tieCheck || {};
    const meta = ws.addRow([
      `Generated: ${new Date().toLocaleString("en-IN")}  |  Tie check (Opening net + changes ≈ Closing): ${
        tie.matched ? "OK" : `Review (Δ ₹${roundMoney((tie.openingPlusChangeNet || 0) - (tie.closingNet || 0))})`
      }`,
    ]);
    meta.font = { size: 9, italic: true, color: { argb: "FF6B7280" } };
    ws.mergeCells(3, 1, 3, Math.max(headers.length, 4));

    const headerRow = ws.addRow(headers);
    headerRow.height = 32;
    headerRow.eachCell((cell) => {
      styleLightHeader(cell, REPORT_COLORS.recon);
      cell.alignment = { ...(cell.alignment || {}), wrapText: true, vertical: "middle", horizontal: "center" };
    });

    // (A) Opening
    pushTotalsRow(ws, reco.opening.label, reco.opening, earn, ded, moneyEndCol);

    // (B) Additions
    const addSec = ws.addRow([reco.additions.sectionTitle]);
    styleSection(addSec);
    try {
      ws.mergeCells(ws.rowCount, 1, ws.rowCount, headers.length);
    } catch {
      /* ignore */
    }
    if (!reco.additions.employees.length) {
      const empty = ws.addRow(["", "(No new joinees)", ...Array(headers.length - 2).fill("")]);
      empty.font = { italic: true, color: { argb: "FF6B7280" } };
    } else {
      reco.additions.employees.forEach((e) => pushEmpRow(ws, e, earn, ded, moneyEndCol));
    }
    pushTotalsRow(ws, reco.additions.totalLabel, reco.additions.totals, earn, ded, moneyEndCol);

    // (C) Deletions
    const delSec = ws.addRow([reco.deletions.sectionTitle]);
    styleSection(delSec);
    try {
      ws.mergeCells(ws.rowCount, 1, ws.rowCount, headers.length);
    } catch {
      /* ignore */
    }
    if (!reco.deletions.employees.length) {
      const empty = ws.addRow(["", "(No employees left)", ...Array(headers.length - 2).fill("")]);
      empty.font = { italic: true, color: { argb: "FF6B7280" } };
    } else {
      reco.deletions.employees.forEach((e) => pushEmpRow(ws, e, earn, ded, moneyEndCol));
    }
    pushTotalsRow(ws, reco.deletions.totalLabel, reco.deletions.totals, earn, ded, moneyEndCol);

    // (D) Adjustments
    const adjSec = ws.addRow([reco.adjustments.sectionTitle]);
    styleSection(adjSec);
    try {
      ws.mergeCells(ws.rowCount, 1, ws.rowCount, headers.length);
    } catch {
      /* ignore */
    }
    if (!reco.adjustments.employees.length) {
      const empty = ws.addRow([
        "",
        "(No continuing-employee amount differences)",
        ...Array(headers.length - 2).fill(""),
      ]);
      empty.font = { italic: true, color: { argb: "FF6B7280" } };
    } else {
      reco.adjustments.employees.forEach((e) => pushEmpRow(ws, e, earn, ded, moneyEndCol));
    }
    pushTotalsRow(ws, reco.adjustments.totalLabel, reco.adjustments.totals, earn, ded, moneyEndCol);

    ws.addRow([]);
    pushTotalsRow(ws, reco.changeTotals.label, reco.changeTotals, earn, ded, moneyEndCol);
    pushTotalsRow(ws, reco.closing.label, reco.closing, earn, ded, moneyEndCol);

    // Reason Summary sheet per compare (first gets primary name)
    const reasonSheetName =
      idx === 0 ? "Reason Summary" : `Reasons ${String(reco.comparePayMonth || "").slice(5)}`.slice(0, 31);
    const wsReason = wb.addWorksheet(reasonSheetName, {
      views: [{ state: "frozen", ySplit: 8, showGridLines: false }],
    });
    const npc = reco.netPayComparison || {};
    const fmtInr = (n) => roundMoney(n).toLocaleString("en-IN");
    const deltaSign = (n) => {
      const v = roundMoney(n);
      return `${v >= 0 ? "+" : ""}${v.toLocaleString("en-IN")}`;
    };

    addTitleBlock(
      wsReason,
      companyName,
      `Reason Summary — ${reco.currentLabel} vs ${reco.compareLabel}`,
      "Net pay comparison and reasoned differentials (amounts should tie to Closing − Opening)",
      5
    );

    const cmpHeader = wsReason.addRow(["Net Pay comparison", "", "", "", ""]);
    cmpHeader.font = { bold: true, color: { argb: "FF1E3A5F" } };
    cmpHeader.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0F2FE" } };
      applyDataBorder(cell);
    });
    try {
      wsReason.mergeCells(wsReason.rowCount, 1, wsReason.rowCount, 5);
    } catch {
      /* ignore */
    }

    const pushCmp = (label, amount, note = "") => {
      const row = wsReason.addRow([label, roundMoney(amount), "", note, ""]);
      row.eachCell((cell, c) => {
        applyDataBorder(cell);
        if (c === 2) moneyFmt(cell);
      });
      return row;
    };
    pushCmp(`Total Net Pay — ${npc.compareLabel || reco.compareLabel} (Opening)`, npc.openingNet);
    pushCmp(`Total Net Pay — ${npc.currentLabel || reco.currentLabel} (Closing)`, npc.closingNet);
    const diffRow = pushCmp(
      "Differential (Closing − Opening)",
      npc.differential,
      npc.matched ? "Reasoned total matches" : "Review reasoned total vs differential"
    );
    diffRow.font = { bold: true };
    pushCmp("Total reasoned amount (by category below)", npc.reasonedTotal);

    wsReason.addRow([]);

    const rh = wsReason.addRow([
      "Reason Category",
      "No. of employees",
      "Reasoned amount (₹)",
      "What this means",
      "Examples",
    ]);
    rh.eachCell((cell) => {
      styleLightHeader(cell, REPORT_COLORS.summary);
      cell.alignment = { wrapText: true, vertical: "middle" };
    });

    (reco.reasonSummary || []).forEach((r) => {
      if (!(Number(r.count) > 0 || Math.abs(Number(r.netImpact) || 0) >= 1)) return;
      const row = wsReason.addRow([
        r.category,
        Number(r.count) || 0,
        roundMoney(r.netImpact),
        r.interpretation || "",
        (r.samples || []).join(" | "),
      ]);
      row.eachCell((cell, c) => {
        applyDataBorder(cell);
        if (c === 2) {
          cell.numFmt = "0";
          cell.alignment = { horizontal: "right" };
        }
        if (c === 3) moneyFmt(cell);
        if (c === 1) cell.font = { bold: true };
      });
    });
    if (!(reco.reasonSummary || []).some((r) => Number(r.count) > 0)) {
      wsReason.addRow(["(No variances)", 0, 0, "Months match for headcount and amounts", ""]);
    }

    const totalsRow = wsReason.addRow([
      "TOTAL reasoned (must match Differential)",
      "",
      roundMoney(npc.reasonedTotal),
      npc.matched
        ? `OK — matches Δ ${deltaSign(npc.differential)}`
        : `Gap ₹${fmtInr(Math.abs(roundMoney(npc.reasonedTotal) - roundMoney(npc.differential)))}`,
      "",
    ]);
    totalsRow.font = { bold: true };
    totalsRow.eachCell((cell, c) => {
      styleLightHeader(cell, npc.matched ? "FFDCFCE7" : "FFFEE2E2");
      if (c === 3) moneyFmt(cell);
    });

    applyHeaderBasedColumnWidths(ws, headers, { layout: "reco" });
    // Reason column only: single-line width from longest reason text
    const reasonTexts = [
      ...(reco.additions.employees || []).map((e) => e.reason),
      ...(reco.deletions.employees || []).map((e) => e.reason),
      ...(reco.adjustments.employees || []).map((e) => e.reason),
      headers[reasonCol - 1],
    ];
    let maxReasonLen = String(headers[reasonCol - 1] || "Reason").length;
    reasonTexts.forEach((t) => {
      const len = String(t || "").length;
      if (len > maxReasonLen) maxReasonLen = len;
    });
    ws.getColumn(reasonCol).width = Math.min(120, Math.max(24, maxReasonLen + 2));
    // Ensure reason cells stay unwrapped / single line
    ws.getColumn(reasonCol).alignment = { wrapText: false, vertical: "middle" };

    applyHeaderBasedColumnWidths(
      wsReason,
      ["Reason Category", "No. of employees", "Reasoned amount (₹)", "What this means", "Examples"],
      { layout: "summary" }
    );
    wsReason.getColumn(1).width = 32;
    wsReason.getColumn(5).width = 40;
  });

  return downloadWorkbook(wb, `Reco Report ${fileLabel}.xlsx`);
}

/** Accounting Journal — Sheet 1 JV (+ cost match) + Sheet 2 Salary Register replica */
export async function exportAccountingJournalExcel(data, options = {}) {
  const rows = data?.rows || [];
  if (!rows.length) throw new Error("No processed employees to export");

  const companyName = getCompanyName();
  const monthLabel = formatPayMonthLabel(data.payMonth);
  const fileLabel = fileMonthLabel(data.payMonth);
  const metaLine = metaFromGate(data);
  const { orderedFixedEarnings, orderedBenefits, orderedPaidFixed, orderedVariables, orderedDeductions } =
    resolveOrders(data, options);

  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();

  buildJvSheet(wb, {
    companyName,
    title: `Accounting Journal Voucher — ${monthLabel}`,
    metaLine,
    jv: data.jv,
    jvGroups: data.jvGroups,
    costMatch: data.costMatch,
  });

  buildRegisterSheet(wb, {
    sheetName: "Salary Register",
    companyName,
    title: `Salary Register (replica) — ${monthLabel}`,
    metaLine,
    rows,
    orderedFixedEarnings,
    orderedBenefits,
    orderedPaidFixed,
    orderedVariables,
    orderedDeductions,
    includeTotalCost: true,
    includeTotalCashFlow: true,
  });

  return downloadWorkbook(wb, `Accounting Journal ${fileLabel}.xlsx`);
}

/** Away-from-payroll leave encashment disbursement register */
export async function exportLeaveEncashmentAwayExcel(data, options = {}) {
  const payMonth = String(data?.payMonth || options.payMonth || "").slice(0, 7);
  const monthLabel = formatPayMonthLabel(payMonth);
  const fileLabel = fileMonthLabel(payMonth);
  const companyName = options.companyName || getCompanyName();
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();
  const ws = wb.addWorksheet("Leave Encashment Away", {
    views: [{ state: "frozen", ySplit: 5, showGridLines: false }],
  });
  const headers = [
    "Sl No",
    "Emp Code",
    "Name",
    "Days",
    "Daily Rate",
    "Amount",
    "Bank Name",
    "Account No",
    "IFSC",
    "Status",
  ];
  addTitleBlock(
    ws,
    companyName,
    `Leave Encashment (Away from Payroll) — ${monthLabel}`,
    `Pay month ${payMonth} · ${rows.length} row(s)`,
    headers.length
  );
  const headerRow = ws.addRow(headers);
  headerRow.eachCell((cell) => styleLightHeader(cell, REPORT_COLORS.bank));
  let total = 0;
  rows.forEach((r, i) => {
    const amt = roundMoney(r.amount);
    total += amt;
    const row = ws.addRow([
      i + 1,
      r.empCode || "",
      r.name || "",
      Number(r.days) || 0,
      roundMoney(r.dailyRate),
      amt,
      r.bankName || "",
      r.accountNo || "",
      r.ifsc || "",
      r.status || "",
    ]);
    row.eachCell((cell, c) => {
      applyDataBorder(cell);
      if (c === 5 || c === 6) moneyFmt(cell);
    });
  });
  const tot = ws.addRow(["", "", "", "", "TOTAL", roundMoney(total), "", "", "", ""]);
  tot.font = { bold: true };
  tot.eachCell((cell, c) => {
    styleLightHeader(cell, "FFF3F4F6");
    if (c === 6) moneyFmt(cell);
  });
  applyHeaderBasedColumnWidths(ws, headers, { layout: "bank" });
  return downloadWorkbook(wb, `Leave Encashment Away ${fileLabel}.xlsx`);
}
